import { Box, Divider, MenuItem, Select, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import InfoTooltip from "../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { SpinnerButton } from "../../../../../common/components/functional/spinnerButton/SpinnerButton";
import { canExpertApproveEntries } from "../../../../user/accessRightChecks";
import {
  EditEntry,
  Entry,
  Leave,
  LeaveEntity,
  LeaveEntityDateRange,
} from "../../../entriesAPI";
import { postResetEntryApprovalState } from "../../../entriesSlice";
import { ABSENCE_TYPES_REQUIRING_ATTACHMENT } from "../../Shared/functions";
import LeaveAttachments from "../../Shared/LeaveAttachments";
import LeaveEntityDateRangePicker from "../../Shared/LeaveEntityDateRangePicker";
import { AbsenceListEntry } from "../AbsenceHoursList/AbsenceHoursList";
import { SICK_LEAVE_TYPES } from "../Absences";
import ContinuePrevious from "./DetailsParts/ContinuePrevious";
import LeaveComment from "./DetailsParts/LeaveComment";
import PreviousLeaves from "./DetailsParts/PreviousLeaves";

interface Props {
  selectedEntry: EditEntry | Entry | null;
  leaveEntity: LeaveEntity;
  setLeaveEntity: (val: LeaveEntity) => void;
  updateLeaveEntity: (
    key: string,
    value: string | LeaveEntityDateRange | null | Leave,
  ) => void;
  editingActive: boolean;
  translatedEntryTypes: { [key: string]: string };
  inputErrors: { [code: string]: boolean };
  setInputErrors: (inputErrors: { [code: string]: boolean }) => void;
  entries: AbsenceListEntry[];
  setEntries: (val: AbsenceListEntry[]) => void;
  pendingAttachments?: File[];
  onPendingAttachmentsChange?: (files: File[]) => void;
}

const SickLeaveDetails = ({
  selectedEntry,
  leaveEntity,
  setLeaveEntity,
  updateLeaveEntity,
  editingActive,
  translatedEntryTypes,
  inputErrors,
  setInputErrors,
  entries,
  setEntries,
  pendingAttachments,
  onPendingAttachmentsChange,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const [isClearingLoading, setIsClearingLoading] = useState(false);

  const checkedEntries = entries.filter((entry) => entry.checked);

  const leaveHasAttachment =
    (leaveEntity?.leaveAttachment?.attachments?.length ?? 0) > 0;

  const allCheckedEntriesRejected =
    checkedEntries.length > 0 &&
    checkedEntries.every((entry) => entry.approvalState === "Rejected");

  const canResetApproval = user ? canExpertApproveEntries(user) : false;

  const isLeaveTypeRequiringAttachment =
    ABSENCE_TYPES_REQUIRING_ATTACHMENT.includes(leaveEntity.type);

  // Show button when: user has rights, leave is saved, all checked entries are rejected,
  // leave type requires attachment, and no attachment has been submitted.
  const showClearRejectionButton =
    canResetApproval &&
    !!leaveEntity.id &&
    allCheckedEntriesRejected &&
    isLeaveTypeRequiringAttachment &&
    !leaveHasAttachment;

  const handleClearRejectedState = async () => {
    const entriesToReset = checkedEntries
      .filter((entry) => entry.approvalState === "Rejected")
      .map((entry) => ({ id: entry.id, version: entry.version }));
    if (entriesToReset.length === 0) return;

    setIsClearingLoading(true);
    const resetAction = await dispatch(
      postResetEntryApprovalState(entriesToReset),
    );
    setIsClearingLoading(false);
    if (!isFulfilled(resetAction)) return;

    const resetEntriesById = new Map(
      resetAction.payload.map((entry) => [
        entry.id,
        {
          version: entry.version,
          approvalState: entry.payload.newState,
        },
      ]),
    );

    const updatedEntries = entries.map((entry) => {
      const resetEntry = resetEntriesById.get(entry.id);
      return resetEntry
        ? {
            ...entry,
            version: resetEntry.version,
            approvalState: resetEntry.approvalState,
          }
        : entry;
    });

    setEntries([...updatedEntries]);
  };

  const absenceTypes = SICK_LEAVE_TYPES.map((key) => ({
    value: key,
    name: translatedEntryTypes[key],
  })).sort((a, b) => a.name.localeCompare(b.name, "fi"));

  return (
    selectedEntry && (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          padding: "24px 16px",
          width: "28%",
          opacity: editingActive ? 0.6 : 1, // Adjust opacity, if entry is being edited
          pointerEvents: editingActive ? "none" : "auto", // Disable pointer events, if entry is being edited
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="header" fontWeight={600}>
            {t("hourEntries:absenceDetails").toUpperCase()}
          </Typography>
          {showClearRejectionButton && (
            <SpinnerButton
              disabled={checkedEntries.length === 0}
              isLoading={isClearingLoading}
              label={t("hourEntries:clearRejectedState")}
              color="error"
              sx={{
                borderRadius: "20px",
                padding: "5px 15px",
                minWidth: "128px",
              }}
              spinnerSx={{ color: "primary.main" }}
              spinnerSize={16}
              onClick={() => void handleClearRejectedState()}
            />
          )}
        </Box>
        <Divider
          sx={{
            marginTop: "16px",
            marginBottom: "16px",
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            marginTop: "16px",
          }}
        >
          <Typography
            variant="header"
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              color: "rgba(0, 0, 0, 0.52)",
              marginBottom: "5px",
            }}
          >
            {t("hourEntries:absenceType")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Select
              size="small"
              fullWidth
              value={leaveEntity.type || ""}
              onChange={(e) => updateLeaveEntity("type", e.target.value)}
              renderValue={() => translatedEntryTypes[leaveEntity.type] || ""}
              sx={{ marginRight: "5px" }}
            >
              {absenceTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
            <InfoTooltip title={t("hourEntries:absenceTypeTooltipSickLeave")} />
          </Box>

          <Typography
            variant="header"
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              color: "rgba(0, 0, 0, 0.52)",
              marginTop: "10px",
              marginBottom: "5px",
            }}
          >
            {t("hourEntries:absenceDateRange")}
          </Typography>

          <LeaveEntityDateRangePicker
            dateRange={leaveEntity.dateRange}
            inputErrors={inputErrors}
            setInputErrors={setInputErrors}
            updateLeaveEntity={updateLeaveEntity}
            disabled={false}
          />
        </Box>

        <LeaveAttachments
          entries={entries}
          setEntries={setEntries}
          leaveEntity={leaveEntity}
          setLeaveEntity={setLeaveEntity}
          pendingAttachments={pendingAttachments}
          onPendingAttachmentsChange={onPendingAttachmentsChange}
        />

        <Box
          sx={{
            marginLeft: "10px",
          }}
        >
          <ContinuePrevious
            leaveEntity={leaveEntity}
            updateLeaveEntity={updateLeaveEntity}
          />
        </Box>

        {leaveEntity.previousLeaves.order.length > 0 && (
          <PreviousLeaves
            leaveEntity={leaveEntity}
            updateLeaveEntity={updateLeaveEntity}
          />
        )}

        {leaveEntity?.comment && <LeaveComment comment={leaveEntity.comment} />}
      </Box>
    )
  );
};

export default SickLeaveDetails;
